import { Sentry } from '@/lib';
import type { ListExperienceEventInput } from '@/types/gql.generated';

/**
 * The server's own default (`DEFAULT_LIMIT` in services/event/queryLib.ts), so an
 * experience whose window fits in one page makes exactly the request it made
 * before paging existed.
 */
export const EVENT_PAGE_SIZE = 200;

/**
 * A backstop for a pathological experience rather than a target: five requests
 * reach 3200 events in a single window. Past that the shortfall is reported
 * instead of walked, because the whole point of paging is that a view never
 * silently drops events.
 */
const MAX_EVENT_REQUESTS = 5;

type FetchPage<E> = (filters: ListExperienceEventInput) => Promise<E[]>;

/**
 * Fetch every event matching `filters`, not just the first page.
 *
 * `Experience.events` returns the earliest N matching events ordered by start
 * date and offers no cursor or offset, so a window holding more than the cap used
 * to come back silently truncated — the events past the cap simply never
 * rendered. `limit` is a ceiling rather than a page size (an untruncated request
 * costs exactly the events in the window), so pages are walked by asking for a
 * bigger slice: a response as long as the limit we asked for may have been cut
 * off, so the request repeats with the limit doubled until one comes back short.
 *
 * Widening rather than advancing a cursor is what the API supports: the date
 * filter matches anything OVERLAPPING the window while the ordering is by start
 * date, so a long-running event (or an unexpanded open-ended recurrence, whose
 * lastEndDate sits in 2037) is re-returned by every window that starts after it
 * — a start-date cursor would spend each page on the same events and never
 * advance.
 */
export const fetchAllExperienceEvents = async <E>(
  filters: ListExperienceEventInput,
  fetchPage: FetchPage<E>
): Promise<E[]> => {
  // An explicit limit is the caller's own ceiling — honor it as asked for.
  if (filters.limit) {
    return fetchPage(filters);
  }

  let limit = EVENT_PAGE_SIZE;
  let events = await fetchPage({ ...filters, limit });
  let requests = 1;

  while (events.length >= limit && requests < MAX_EVENT_REQUESTS) {
    limit *= 2;
    events = await fetchPage({ ...filters, limit });
    requests += 1;
  }

  if (events.length >= limit) {
    Sentry.captureMessage(
      'Experience events query hit its page budget; the view is missing events',
      { extra: { filters, limit, requests } }
    );
  }

  return events;
};
